import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { monthLabel } from "@/lib/format";
import { Money } from "@/components/Money";

const kfmt = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`);

const NetDot = ({ cx, cy, payload }) => (
  <circle cx={cx} cy={cy} r={4} stroke="#fff" strokeWidth={1.5} fill={payload.net < 0 ? "#DC2626" : "#16A34A"} />
);

const TipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="mb-1 font-semibold text-slate-800">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-6">
          <span style={{ color: p.color }}>{p.name}</span>
          <Money value={p.value} className={p.value < 0 ? "" : "text-slate-800"} />
        </div>
      ))}
    </div>
  );
};

export const TrendChart = ({ trend }) => {
  const data = (trend || []).map((p) => ({
    label: monthLabel(p.period, true),
    revenue: p.revenue,
    costs: p.total_expenses,
    net: p.net_profit,
  }));
  const empty = trend && data.every((d) => !d.revenue && !d.costs && !d.net);

  return (
    <div className="rise-in rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="trend-chart">
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="text-base font-semibold text-slate-900 md:text-lg">12-month trend</h2>
        <span className="text-xs text-slate-500">Revenue vs COGS + Expenses vs Net Profit</span>
      </div>
      {!trend ? (
        <Skeleton className="h-72 w-full" />
      ) : empty ? (
        <div className="ledger-grid flex h-72 items-center justify-center rounded-md text-sm text-slate-500" data-testid="trend-chart-empty">No data for the last 12 months yet.</div>
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
              <YAxis tickFormatter={kfmt} tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} width={44} />
              <Tooltip content={<TipBox />} />
              <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine y={0} stroke="#94A3B8" />
              <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#0D9488" strokeWidth={2.25} dot={{ r: 3 }} isAnimationActive={false} />
              <Line type="monotone" dataKey="costs" name="COGS + Expenses" stroke="#64748B" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} isAnimationActive={false} />
              <Line type="monotone" dataKey="net" name="Net Profit" stroke="#16A34A" strokeWidth={2.75} dot={<NetDot />} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
